import React, { useState, useMemo } from 'react'
import { marked } from 'marked'
import hljs from 'highlight.js'
import { PARTICIPANT_COLORS, hashString, MYAGENT_PARTICIPANT_ID, MYAGENT_COLOR } from './participantColors.js'
import './Message.css'

// Configure marked for code highlighting + XSS prevention
const escapeHtml = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

marked.setOptions({
  highlight: function(code, lang) {
    if (lang && hljs.getLanguage(lang)) {
      try {
        return hljs.highlight(code, { language: lang }).value
      } catch (e) {
        console.error('Highlight error:', e)
      }
    }
    return hljs.highlightAuto(code).value
  },
  breaks: true,
  gfm: true,
})

// XSS prevention: escape raw HTML blocks + block javascript: links
const escapeAttr = (s) => escapeHtml(s).replace(/"/g, '&quot;')
marked.use({
  renderer: {
    html(token) { return escapeHtml(token.text) },
    link(token) {
      const href = (token.href || '').trim()
      if (/^\s*javascript\s*:/i.test(href)) return escapeHtml(token.text)
      const title = token.title ? ` title="${escapeAttr(token.title)}"` : ''
      return `<a href="${escapeAttr(href)}"${title} target="_blank" rel="noopener noreferrer">${token.tokens ? this.parser.parseInline(token.tokens) : escapeHtml(token.text)}</a>`
    },
  },
})

/**
 * Message Component
 *
 * Displays a single chat message with markdown support for assistant messages
 * and structured display for worker role messages.
 *
 * @param {Object} props
 * @param {string} props.role - 'user', 'assistant', or 'participant'
 * @param {string} props.content - Message content
 * @param {number} props.timestamp - Unix timestamp
 * @param {boolean} props.isError - Whether this is an error message
 * @param {Object} props.metadata - Message metadata
 * @param {string} props.participantName - Display name for participant messages (resolved from participantId)
 * @param {string} props.participantTypeName - Type name for participant (e.g. 'Chrome Extension')
 * @param {string} props.targetName - Target display name for role_request messages
 */
export const Message = ({ role, content, timestamp, isError = false, metadata, participantId, participantName, participantTypeName, targetName }) => {
  const isUser = role === 'user'
  const isParticipant = role === 'participant'
  const participantColor = useMemo(
    () => {
      if (!isParticipant || !participantId) return null
      if (participantId === MYAGENT_PARTICIPANT_ID) return MYAGENT_COLOR
      return PARTICIPANT_COLORS[hashString(participantId) % PARTICIPANT_COLORS.length]
    },
    [isParticipant, participantId]
  )

  const renderContent = () => {
    if (isUser) {
      return <p className="message-text">{content}</p>
    }

    if (isParticipant) {
      // Tool calls and role requests: monospace with collapsible result
      if (metadata?.type === 'tool_call' || metadata?.type === 'role_request') {
        return <ParticipantContent content={content} metadata={metadata} targetName={targetName} />
      }
      // All other participant messages: markdown rendering
      if (!content) return <p className="message-text message-empty">(No content)</p>
      const html = marked(content)
      return <div className="message-text message-markdown" dangerouslySetInnerHTML={{ __html: html }} />
    }

    // Legacy assistant fallback
    if (!content) return <p className="message-text message-empty">(No content)</p>
    const html = marked(content)
    return <div className="message-text message-markdown" dangerouslySetInnerHTML={{ __html: html }} />
  }

  const renderMetadata = () => {
    if (isUser || !metadata) return null
    if (isParticipant && metadata?.type !== 'assistant_response') return null

    const { usage, modelInfo } = metadata
    const hasData = usage || modelInfo

    if (!hasData) return null

    const parts = []

    if (modelInfo?.selectedModel) {
      parts.push(modelInfo.selectedModel)
    }

    if (usage?.totalTokens) {
      parts.push(`${usage.totalTokens.toLocaleString()} tokens`)
    }

    if (metadata.durationMs) {
      const sec = (metadata.durationMs / 1000).toFixed(1)
      parts.push(`${sec}s`)
    }

    if (parts.length === 0) return null

    const tooltipParts = []
    if (modelInfo?.selectedModel) {
      tooltipParts.push(`Model: ${modelInfo.selectedModel}`)
      if (modelInfo.reason) tooltipParts.push(`Reason: ${modelInfo.reason}`)
    }
    if (usage) {
      if (usage.promptTokens) tooltipParts.push(`Input: ${usage.promptTokens.toLocaleString()}`)
      if (usage.completionTokens) tooltipParts.push(`Output: ${usage.completionTokens.toLocaleString()}`)
      if (usage.totalTokens) tooltipParts.push(`Total: ${usage.totalTokens.toLocaleString()}`)
    }

    return (
      <div className="message-metadata" title={tooltipParts.join('\n')}>
        {parts.join(' \u00b7 ')}
      </div>
    )
  }

  const roleLabel = isUser ? 'You' : (participantName || 'MyAgent')
  const showTypeName = isParticipant && participantTypeName && participantName !== participantTypeName
  const isGenerating = isParticipant && metadata?.type === 'progress'

  return (
    <div
      className={`message message-${role} ${isError ? 'message-error' : ''}`}
      style={participantColor ? { '--participant-color': participantColor } : undefined}
    >
      <div className="message-header">
        <span className="message-role">
          {roleLabel}{showTypeName && <span className="message-role-type"> ({participantTypeName})</span>}
        </span>
        {timestamp && (
          <span className="message-time">
            {new Date(timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        )}
      </div>
      <div className="message-content">
        {renderContent()}
        {isGenerating && <div className="message-generating">Generating<span className="generating-dots" /></div>}
        {renderMetadata()}
      </div>
    </div>
  )
}

/**
 * Participant message content — shows action and collapsible result
 */
const ParticipantContent = ({ content, metadata, targetName }) => {
  const [expanded, setExpanded] = useState(false)
  const hasResult = metadata?.result !== undefined
  const isRequest = metadata?.type === 'role_request'

  return (
    <div className="participant-content">
      <div className="participant-action">
        <span className="participant-action-text">{isRequest && targetName ? `@${targetName} ` : ''}{content}</span>
        {hasResult && (
          <button
            className="participant-detail-toggle"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
          >
            {expanded ? '\u25BE' : '\u25B8'}
          </button>
        )}
      </div>
      {expanded && hasResult && (
        <pre className="participant-result">
          {typeof metadata.result === 'string'
            ? metadata.result
            : JSON.stringify(metadata.result, null, 2)}
        </pre>
      )}
    </div>
  )
}
